import { t, pick, locale, COMMUNITIES } from './i18n.js'
import { mediaUrl, usingSampleData } from './data.js'
import { REPO_URL } from './config.js'

/* ── Small helpers ───────────────────────────────────────────────────────── */

/** Escape before interpolating into HTML. Every value that reaches these
    templates is contributor-supplied and the map publishes instantly, so this
    is the difference between an open archive and a stored-XSS delivery
    mechanism. There is no path here that may skip it. */
export function esc(s) {
  return String(s ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#39;')
}

let toastTimer
export function toast(message, isError = false) {
  const el = document.getElementById('toast')
  el.textContent = message
  el.classList.toggle('error', isError)
  el.hidden = false
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { el.hidden = true }, isError ? 6000 : 3500)
}

const yearRange = (s) => {
  if (s.year_start && s.year_end && s.year_start !== s.year_end) return `${s.year_start}–${s.year_end}`
  return s.year_start || s.year_end || ''
}

const communityNames = (ids = []) =>
  ids.map((id) => t(COMMUNITIES.find((c) => c.id === id)?.key ?? 'c_other')).join('、')

/* ── Detail panel ────────────────────────────────────────────────────────── */

export function renderDetail(s, { onClose, onReport }) {
  const el = document.getElementById('detail')
  const title = pick(s, 'title')
  const narrative = pick(s, 'narrative')

  // The other language, shown underneath when it exists and differs. A
  // bilingual corpus should not hide half of itself behind a toggle.
  const otherNarrative = locale() === 'zh' ? s.narrative_en : s.narrative_zh
  const audio = mediaUrl(s.audio_path)
  const period = yearRange(s)

  const rows = []
  if (s.narrator_label) rows.push([t('narrator'), esc(s.narrator_label)])
  if (period) rows.push([t('period'), esc(period)])
  if (s.communities?.length) rows.push([t('community'), esc(communityNames(s.communities))])
  if (s.species?.length) rows.push([t('species_label'), esc(s.species.join('、'))])
  if (s.contributor_name) rows.push([t('recorded_by'), esc(s.contributor_name)])
  if (s.source_note) rows.push([t('source'), esc(s.source_note)])

  const journey = s.kind === 'relocation' && s.dest_lng != null
    ? `<div class="journey-note">
         <span>${esc(pick(s, 'place') || t('moved_from'))}</span>
         <span class="arrow" aria-hidden="true">→</span>
         <span>${esc(pick(s, 'dest_place') || t('moved_to'))}</span>
         ${s.displacement_km != null
           ? `<span style="margin-left:auto">${s.displacement_km.toFixed(1)} km</span>` : ''}
       </div>`
    : ''

  const photos = (s.photo_paths ?? []).filter(Boolean)

  el.innerHTML = `
    <button class="panel-close" aria-label="Close">✕</button>
    <div class="panel-inner">
      <span class="kind-tag kind-${esc(s.kind)}">${esc(t('k_' + s.kind))}</span>
      <h3>${esc(title)}</h3>
      <p class="subtitle">${esc([pick(s, 'place'), period].filter(Boolean).join(' · '))}</p>
      ${journey}
      ${audio ? `<audio controls preload="none" src="${esc(audio)}"></audio>` : ''}
      ${narrative ? `<p class="narrative">${esc(narrative)}</p>` : ''}
      ${otherNarrative && otherNarrative !== narrative
        ? `<p class="narrative secondary">${esc(otherNarrative)}</p>` : ''}
      ${s.ecology_change
        ? `<h4 style="font-size:.72rem;letter-spacing:.12em;text-transform:uppercase;color:#8aa2ad;margin:1rem 0 .3rem">
             ${esc(t('ecology_change_label'))}</h4>
           <p style="font-size:.88rem;line-height:1.6;margin:0">${esc(s.ecology_change)}</p>` : ''}
      ${photos.length
        ? `<div class="photos">${photos
            .map((p) => `<img loading="lazy" src="${esc(mediaUrl(p))}" alt="" />`).join('')}</div>` : ''}
      ${rows.length
        ? `<dl class="meta-grid">${rows
            .map(([k, v]) => `<dt>${esc(k)}</dt><dd>${v}</dd>`).join('')}</dl>` : ''}
      ${(pick(s, 'transcript'))
        ? `<details class="transcript">
             <summary>${esc(t('transcript'))}</summary>
             <div class="body">${esc(pick(s, 'transcript'))}</div>
           </details>` : ''}
      ${s.precision_level === 'withheld'
        ? `<p class="withheld-note">${esc(t('withheld_note'))}</p>` : ''}
      <div class="panel-actions">
        <button class="ghost small" data-act="share">${esc(t('share'))}</button>
        <button class="ghost small" data-act="report">${esc(t('report'))}</button>
      </div>
    </div>`

  el.hidden = false
  el.querySelector('.panel-close').onclick = onClose
  el.querySelector('[data-act="share"]').onclick = async () => {
    const url = new URL(location.href)
    url.searchParams.set('story', s.id)
    await navigator.clipboard.writeText(url.toString())
    toast(t('link_copied'))
  }
  el.querySelector('[data-act="report"]').onclick = () => onReport(s.id)
}

export function closeDetail() {
  const el = document.getElementById('detail')
  el.hidden = true
  el.innerHTML = ''
}

/* ── Modal shell ─────────────────────────────────────────────────────────── */

let escHandler = null

export function openModal(html, { onMount } = {}) {
  const host = document.getElementById('modal-host')
  host.innerHTML = `<div class="modal"><div class="modal-inner">${html}</div></div>`
  host.hidden = false

  // Click the backdrop, not the card, to dismiss.
  host.onclick = (e) => { if (e.target === host) closeModal() }
  escHandler = (e) => { if (e.key === 'Escape') closeModal() }
  document.addEventListener('keydown', escHandler)

  onMount?.(host.querySelector('.modal'))
  host.querySelector('.modal').scrollTop = 0
}

export function closeModal() {
  const host = document.getElementById('modal-host')
  host.hidden = true
  host.innerHTML = ''
  if (escHandler) document.removeEventListener('keydown', escHandler)
  escHandler = null
}

/** Hide the modal without destroying it, so form state survives a trip to the
    map to pick a point. */
export function suspendModal() { document.getElementById('modal-host').style.display = 'none' }
export function resumeModal()  { document.getElementById('modal-host').style.display = '' }
export const modalIsOpen = () => !document.getElementById('modal-host').hidden

/* ── About ───────────────────────────────────────────────────────────────── */

export function openAbout() {
  const zh = locale() === 'zh'
  openModal(`
    <h2>${zh ? '關於潮界' : 'About Tideline'}</h2>
    <p class="lede">${zh
      ? '潮界是一個公眾參與的地圖，記錄香港填海如何改變海岸線，以及蜑家人、水上人、鶴佬人如何被迫遷離水面。'
      : 'Tideline is a public map recording how reclamation redrew Hong Kong’s coastline, and how the Tanka, boat-dwelling and Hoklo communities were moved off the water.'}</p>

    ${usingSampleData ? `
    <div class="consent-box">
      <h3>${zh ? '示範資料' : 'Seed data'}</h3>
      <p style="margin:0">${zh
        ? '此站目前顯示八條有出處的文獻記錄，取自人口普查、報章與檔案，每條均附引用，可自行覆核。它們並非虛構，但同樣不是口述歷史 —— 沒有任何一條是有人親口說過的證言，所以都沒有敘述者。連接資料庫後，真實訪談將與它們並存。'
        : 'This site is currently showing eight sourced documentary records, drawn from censuses, newspapers and archives, each one cited so you can check it. They are not invented — but they are not oral history either: not one is testimony anybody gave, which is why none of them carries a narrator. Real interviews will sit alongside them once a database is connected.'}</p>
    </div>` : ''}

    <h3>${zh ? '如何使用' : 'How to use it'}</h3>
    <p>${zh
      ? '拖動下方時間軸選擇年代，左側可切換圖層與族群篩選。按「記錄故事」在地圖上加入你的記憶 —— 內容會即時公開。'
      : 'Drag the timeline to choose a period; use the left rail to toggle layers and filter by community. Press “Add a story” to place your own memory on the map — it publishes immediately.'}</p>

    <h3>${zh ? '倫理' : 'Ethics'}</h3>
    <p>${zh
      ? '請只分享你有權分享的內容。若記錄他人的口述，請先取得同意，並可選擇不公開確切位置。任何涉及私隱或同意的回報會即時隱藏該記錄，再交由研究團隊處理。'
      : 'Please only share what is yours to share. If you are recording someone else’s account, get their consent first, and consider withholding the exact location. Any report raising privacy or consent hides the record immediately, pending review by the research team.'}</p>

    <h3>${zh ? '授權與資料' : 'Licence and data'}</h3>
    <p>${zh
      ? '程式碼以 MIT 授權開放，內容以 CC BY-SA 4.0 授權。所有記錄可匯出為 GeoJSON 或 CSV。'
      : 'The code is open source under MIT; the contributed content is licensed CC BY-SA 4.0. Every record can be exported as GeoJSON or CSV.'}
      ${REPO_URL
        ? `<a href="${esc(REPO_URL)}" target="_blank" rel="noopener">${zh ? '原始碼' : 'Source code'} ↗</a>`
        : ''}</p>

    <div class="modal-actions">
      <span class="spacer"></span>
      <button class="btn-primary" data-act="close">${zh ? '關閉' : 'Close'}</button>
    </div>`,
  { onMount: (m) => { m.querySelector('[data-act="close"]').onclick = closeModal } })
}

/* ── Report ──────────────────────────────────────────────────────────────── */

export function openReport(storyId, onSubmit) {
  const reasons = ['spam', 'abuse', 'privacy', 'consent', 'inaccurate', 'other']
  openModal(`
    <h2>${esc(t('report_title'))}</h2>
    <p class="lede">${esc(t('report_lede'))}</p>
    <div class="field">
      <select id="report-reason">
        ${reasons.map((r) => `<option value="${r}">${esc(t('r_' + r))}</option>`).join('')}
      </select>
    </div>
    <div class="field">
      <label for="report-detail">${esc(t('r_detail'))}</label>
      <textarea id="report-detail"></textarea>
    </div>
    <div class="modal-actions">
      <button class="ghost" data-act="cancel">${esc(t('cancel'))}</button>
      <span class="spacer"></span>
      <button class="btn-primary" data-act="send">${esc(t('report_send'))}</button>
    </div>`,
  { onMount: (m) => {
      m.querySelector('[data-act="cancel"]').onclick = closeModal
      m.querySelector('[data-act="send"]').onclick = async (e) => {
        e.target.disabled = true
        try {
          await onSubmit(
            storyId,
            m.querySelector('#report-reason').value,
            m.querySelector('#report-detail').value.trim()
          )
          closeModal()
          toast(t('report_ok'))
        } catch (err) {
          e.target.disabled = false
          toast(err.message || t('err_network'), true)
        }
      }
    } })
}

/* ── Submission form ─────────────────────────────────────────────────────── */

/**
 * The form is deliberately long. An oral-history record with no provenance and
 * no consent flag is close to useless to the research and risky to the person
 * it describes, so those fields are not tucked behind an "advanced" toggle.
 * What IS optional stays visibly optional.
 */
export function openSubmit({ picked, onPick, onSubmit }) {
  const zh = locale() === 'zh'
  const yearNow = new Date().getFullYear()

  const coordText = (p) => p
    ? `<span class="coords">${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}</span>`
    : `<span>${esc(t('f_no_point'))}</span>`

  openModal(`
    <h2>${esc(t('form_title'))}</h2>
    <p class="lede">${esc(t('form_lede'))}</p>

    <div class="field">
      <label for="f-kind">${esc(t('f_kind'))}</label>
      <select id="f-kind">
        <option value="story">${esc(t('f_kind_story'))}</option>
        <option value="relocation">${esc(t('f_kind_relocation'))}</option>
        <option value="ecology">${esc(t('f_kind_ecology'))}</option>
        <option value="place">${esc(t('f_kind_place'))}</option>
      </select>
    </div>

    <div class="field-row">
      <div class="field">
        <label for="f-title-zh">${esc(t('f_title_zh'))}</label>
        <input type="text" id="f-title-zh" maxlength="200" />
      </div>
      <div class="field">
        <label for="f-title-en">${esc(t('f_title_en'))}</label>
        <input type="text" id="f-title-en" maxlength="200" />
      </div>
    </div>

    <div class="field">
      <label for="f-narrative-zh">${esc(t('f_narrative_zh'))}</label>
      <textarea id="f-narrative-zh" maxlength="20000"></textarea>
    </div>
    <div class="field">
      <label for="f-narrative-en">${esc(t('f_narrative_en'))}</label>
      <textarea id="f-narrative-en" maxlength="20000"></textarea>
      <p class="help">${esc(t('f_narrative_help'))}</p>
    </div>

    <h3>${esc(t('f_where'))}</h3>
    <div class="field">
      <div class="pick-box">
        <button type="button" class="ghost small" data-pick="origin">${esc(t('f_pick_origin'))}</button>
        <span id="origin-readout">${coordText(picked.origin)}</span>
      </div>
    </div>
    <div class="field">
      <label for="f-place">${esc(t('f_place_name'))}</label>
      <input type="text" id="f-place" maxlength="200" />
    </div>

    <div id="dest-block" hidden>
      <div class="field">
        <div class="pick-box">
          <button type="button" class="ghost small" data-pick="dest">${esc(t('f_pick_dest'))}</button>
          <span id="dest-readout">${coordText(picked.dest)}</span>
        </div>
      </div>
      <div class="field">
        <label for="f-dest-place">${esc(t('f_dest_place_name'))}</label>
        <input type="text" id="f-dest-place" maxlength="200" />
      </div>
    </div>

    <div class="field">
      <label for="f-precision">${esc(t('f_precision'))}</label>
      <select id="f-precision">
        <option value="approximate">${esc(t('p_approximate'))}</option>
        <option value="exact">${esc(t('p_exact'))}</option>
        <option value="area">${esc(t('p_area'))}</option>
        <option value="withheld">${esc(t('p_withheld'))}</option>
      </select>
      <p class="help">${esc(t('f_precision_help'))}</p>
    </div>

    <h3>${esc(t('f_when'))}</h3>
    <div class="field-row">
      <div class="field">
        <label for="f-year-start">${esc(t('f_year_start'))}</label>
        <input type="number" id="f-year-start" min="1800" max="${yearNow}" />
      </div>
      <div class="field">
        <label for="f-year-end">${esc(t('f_year_end'))}</label>
        <input type="number" id="f-year-end" min="1800" max="${yearNow}" />
      </div>
    </div>
    <p class="help">${esc(t('f_year_help'))}</p>

    <h3>${esc(t('f_communities'))}</h3>
    <div class="chip-set" id="f-communities">
      ${COMMUNITIES.map((c) =>
        `<button type="button" class="chip" data-community="${c.id}" aria-pressed="false">${esc(t(c.key))}</button>`
      ).join('')}
    </div>

    <div id="ecology-block" hidden>
      <div class="field" style="margin-top:1rem">
        <label for="f-species">${esc(t('f_species'))}</label>
        <input type="text" id="f-species" />
        <p class="help">${esc(t('f_species_help'))}</p>
      </div>
      <div class="field">
        <label for="f-ecology-change">${esc(t('f_ecology_change'))}</label>
        <textarea id="f-ecology-change"></textarea>
      </div>
    </div>

    <h3>${esc(t('f_media'))}</h3>
    <div class="field">
      <label for="f-audio">${esc(t('f_audio'))}</label>
      <input type="file" id="f-audio" accept="audio/*" />
    </div>
    <div class="field">
      <label for="f-photos">${esc(t('f_photos'))}</label>
      <input type="file" id="f-photos" accept="image/*" multiple />
    </div>
    <div class="field-row">
      <div class="field">
        <label for="f-transcript-zh">${esc(t('f_transcript_zh'))}</label>
        <textarea id="f-transcript-zh"></textarea>
      </div>
      <div class="field">
        <label for="f-transcript-en">${esc(t('f_transcript_en'))}</label>
        <textarea id="f-transcript-en"></textarea>
      </div>
    </div>

    <h3>${esc(t('f_provenance'))}</h3>
    <div class="field-row">
      <div class="field">
        <label for="f-narrator">${esc(t('f_narrator'))}</label>
        <input type="text" id="f-narrator" maxlength="120" />
        <p class="help">${esc(t('f_narrator_help'))}</p>
      </div>
      <div class="field">
        <label for="f-birth-year">${esc(t('f_birth_year'))}</label>
        <input type="number" id="f-birth-year" min="1850" max="${yearNow}" />
      </div>
    </div>
    <div class="field-row">
      <div class="field">
        <label for="f-contributor">${esc(t('f_contributor'))}</label>
        <input type="text" id="f-contributor" maxlength="120" />
      </div>
      <div class="field">
        <label for="f-contact">${esc(t('f_contact'))}</label>
        <input type="email" id="f-contact" maxlength="200" />
        <p class="help">${esc(t('f_contact_help'))}</p>
      </div>
    </div>
    <div class="field">
      <label for="f-source">${esc(t('f_source_note'))}</label>
      <input type="text" id="f-source" maxlength="400" />
      <p class="help">${esc(t('f_source_help'))}</p>
    </div>

    <div class="consent-box">
      <h3>${esc(t('consent_title'))}</h3>
      <label class="checkbox-field">
        <input type="checkbox" id="f-consent-1" />
        <span>${esc(t('consent_1'))}</span>
      </label>
      <label class="checkbox-field" style="margin-bottom:0">
        <input type="checkbox" id="f-consent-2" />
        <span>${esc(t('consent_2'))}</span>
      </label>
    </div>

    <p class="form-error" id="f-error" hidden></p>

    <div class="modal-actions">
      <button class="ghost" data-act="cancel">${esc(t('cancel'))}</button>
      <span class="spacer"></span>
      <button class="btn-primary" data-act="submit">${esc(t('submit'))}</button>
    </div>`,
  { onMount: (m) => {
      const $ = (sel) => m.querySelector(sel)
      const kindSel = $('#f-kind')

      // Show only the fields the chosen record type actually needs. A memory of
      // a place should not be asked for a destination.
      const syncKind = () => {
        $('#dest-block').hidden = kindSel.value !== 'relocation'
        $('#ecology-block').hidden = kindSel.value !== 'ecology'
      }
      kindSel.onchange = syncKind
      syncKind()

      m.querySelectorAll('[data-community]').forEach((chip) => {
        chip.onclick = () =>
          chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true')
      })

      m.querySelectorAll('[data-pick]').forEach((btn) => {
        btn.onclick = () => onPick(btn.dataset.pick, collect(m))
      })

      $('[data-act="cancel"]').onclick = closeModal
      $('[data-act="submit"]').onclick = async (e) => {
        const err = $('#f-error')
        err.hidden = true
        const draft = collect(m)

        const problem = validate(draft, picked)
        if (problem) { err.textContent = problem; err.hidden = false; return }

        e.target.disabled = true
        e.target.textContent = t('submitting')
        try {
          await onSubmit(draft, {
            audio: $('#f-audio').files[0] ?? null,
            photos: [...$('#f-photos').files],
          })
        } catch (submitErr) {
          e.target.disabled = false
          e.target.textContent = t('submit')
          err.textContent = submitErr.message || t('err_network')
          err.hidden = false
        }
      }
    } })
}

/** Read the form into a plain object. Called both on submit and before a trip
    to the map, so nothing is lost when the modal is suspended. */
export function collect(m) {
  const val = (sel) => m.querySelector(sel)?.value.trim() || null
  const num = (sel) => {
    const v = m.querySelector(sel)?.value
    return v ? Number(v) : null
  }
  return {
    kind: val('#f-kind') ?? 'story',
    title_zh: val('#f-title-zh'),
    title_en: val('#f-title-en'),
    narrative_zh: val('#f-narrative-zh'),
    narrative_en: val('#f-narrative-en'),
    place_zh: val('#f-place'),
    place_en: val('#f-place'),
    dest_place_zh: val('#f-dest-place'),
    dest_place_en: val('#f-dest-place'),
    precision_level: val('#f-precision') ?? 'approximate',
    year_start: num('#f-year-start'),
    year_end: num('#f-year-end'),
    communities: [...m.querySelectorAll('[data-community][aria-pressed="true"]')]
      .map((c) => c.dataset.community),
    species: (val('#f-species') ?? '').split(/[,，;；]/).map((s) => s.trim()).filter(Boolean),
    ecology_change: val('#f-ecology-change'),
    transcript_zh: val('#f-transcript-zh'),
    transcript_en: val('#f-transcript-en'),
    narrator_label: val('#f-narrator'),
    narrator_birth_year: num('#f-birth-year'),
    contributor_name: val('#f-contributor'),
    contributor_contact: val('#f-contact'),
    source_note: val('#f-source'),
    consent_public: m.querySelector('#f-consent-1')?.checked && m.querySelector('#f-consent-2')?.checked,
    _consent1: m.querySelector('#f-consent-1')?.checked ?? false,
    _consent2: m.querySelector('#f-consent-2')?.checked ?? false,
  }
}

function validate(d, picked) {
  if (!d.title_zh && !d.title_en) return t('err_title')
  if (!picked.origin) return t('err_point')
  if (d.kind === 'relocation' && !picked.dest) return t('err_dest')
  if (!d._consent1 || !d._consent2) return t('err_consent')
  return null
}

/** Re-apply a saved draft after the modal is rebuilt. */
export function restore(m, d) {
  if (!d) return
  const set = (sel, v) => { const el = m.querySelector(sel); if (el && v != null) el.value = v }
  set('#f-kind', d.kind)
  set('#f-title-zh', d.title_zh); set('#f-title-en', d.title_en)
  set('#f-narrative-zh', d.narrative_zh); set('#f-narrative-en', d.narrative_en)
  set('#f-place', d.place_zh); set('#f-dest-place', d.dest_place_zh)
  set('#f-precision', d.precision_level)
  set('#f-year-start', d.year_start); set('#f-year-end', d.year_end)
  set('#f-species', (d.species ?? []).join(', '))
  set('#f-ecology-change', d.ecology_change)
  set('#f-transcript-zh', d.transcript_zh); set('#f-transcript-en', d.transcript_en)
  set('#f-narrator', d.narrator_label); set('#f-birth-year', d.narrator_birth_year)
  set('#f-contributor', d.contributor_name); set('#f-contact', d.contributor_contact)
  set('#f-source', d.source_note)
  if (m.querySelector('#f-consent-1')) m.querySelector('#f-consent-1').checked = d._consent1
  if (m.querySelector('#f-consent-2')) m.querySelector('#f-consent-2').checked = d._consent2
  ;(d.communities ?? []).forEach((id) => {
    m.querySelector(`[data-community="${id}"]`)?.setAttribute('aria-pressed', 'true')
  })
  m.querySelector('#f-kind')?.dispatchEvent(new Event('change'))
}
